import { BrowserAdapter } from "./browserAdapter";
import { css, html, render } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { Tree, NodeModel, TreePointerEvent, TreeEvent } from "@omegagrid/tree";
import { ref, createRef } from "lit/directives/ref.js";
import { ListSelectEvent, Menu, MenuItem, Tooltip, dom, Location } from "@omegagrid/core";
import { AppComponent, ComponentState } from "../component";
import { Application } from "../container";

export class BrowserEvent extends Event {
	browser: Browser;
	node?: NodeModel;

	constructor(type: string, args?: Partial<BrowserEvent>) {
		super(`browser.${type}`, {bubbles: true, composed: true});
		Object.assign(this, args);
	}
}

export type BrowserState = {
	expanded?: string[]
} & ComponentState;

export type BrowserMenuItem = MenuItem & {
	visible?: (node: NodeModel) => boolean
}

@customElement('rp-browser')
export class Browser extends AppComponent<Application, BrowserState> {

	static styles = css`
		* {
			box-sizing: border-box;
		}

		:host {
			display: block;
			height: 100%;
			overflow: hidden;
		}

		og-tree {
			height: 100%;
		}
	`;

	@query('og-tree')
	tree: Tree;

	@property({type: Object})
	adapter: BrowserAdapter;

	tooltipRef = createRef<Tooltip>();
	get tooltip() { return this.tooltipRef?.value }

	menuRef = createRef<Menu>();
	get menu() { return this.menuRef.value }

	private _currentNode: NodeModel;
	private _hideTimer: number;

	constructor() {
		super();
		dom.debounceOn(this, 'mousemove', '.item', this._onMouseMove, {wait: 5, noMatchCallback: () => {
			this.tooltip.deatach();
		}});
		dom.on(this, 'mouseout', '.item', () => this.requestHide());
		dom.on(this, 'mouseleave', '.item', () => this.requestHide());
		dom.on(this, 'contextmenu', '.item', () => this.requestHide());
	}

	requestHide() {
		if (this._hideTimer == null) {
			this._hideTimer = window.setTimeout(() => {
				this.tooltip.deatach();
				this._hideTimer = null;
			}, 100)
		}
	}

	willUpdate(props: Map<string, unknown>) {
		super.willUpdate(props);
		if (props.has('adapter')) {
			this.adapter.browser = this;
		}
	}

	updated() {
		if (this.tooltip.parentElement !== document.body) {
			document.body.appendChild(this.tooltip);
		}
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.tooltip && this.tooltip.parentElement !== document.body) {
			document.body.appendChild(this.tooltip);
		}
	}

	disconnectedCallback() {
		this.shadowRoot.appendChild(this.tooltip);
	}

	_onMouseMove = (e: MouseEvent, elm: HTMLElement) => {
		const index = parseInt(elm.dataset.node ?? '-1');
		if (index < 0) return this.tooltip.deatach();

		const node = this.tree.model.nodes[index];
		if (!node) return this.tooltip.deatach();

		const tooltip = this.adapter.getTooltip(node);
		if (!tooltip) return this.tooltip.deatach();

		const body = this.tooltip.querySelector('[slot="body"]') as HTMLDivElement;
		render(tooltip, body);
		this.tooltip.attachTo(elm, 'right');
	}

	layout() { }

	_onSelect = (e: TreeEvent) => {
		if (e.node.hasChildren) return;
		this.dispatchEvent(new BrowserEvent('select', {browser: this, node: e.node}));
	}

	async openMenu(node: NodeModel, pos: HTMLElement|Location) {
		this._currentNode = node;
		const items = await this.adapter.getMenuItems(node);
		if (items && items.length > 0) {
			this.menu.items = items.filter(item => item?.visible == null || item.visible(node));
			if (pos instanceof HTMLElement) {
				this.menu.open(pos, 'right');
			} else {
				this.menu.openAt(pos.x, pos.y);
			}
		}
	}

	_onMenu = async (e: TreePointerEvent) => {
		this.openMenu(e.node, {x: e.pageX, y: e.pageY});
	}

	_onMenuItemSelect = async (e: ListSelectEvent) => {
		const item = this.menu.items[e.index];
		await this.adapter.onAction(item.key as string, this._currentNode);
		this.tree.renderItems(true);
	}

	getTreeState(): BrowserState {
		const expanded: string[] = [];
		this.tree.model.root.walk((node: NodeModel) => {
			if (node.hasChildren && !node.collapsed) expanded.push(node.key);
		});
		return { expanded };
	}

	setTreeState(state: BrowserState) {
		const expanded = new Set(state.expanded);
		this.tree.model.root.walk((node: NodeModel) => {
			if (node.hasChildren) {
				if (expanded.has(node.key)) node.expand();
				else node.collapse();
			}
		});
		this.tree.model.indexVisibleNodes();
		this.tree.renderItems(true);
	}

	setState(state: BrowserState) {
		this.state = state;
		if (this.tree) this.setTreeState(state);
	}

	getState() {
		if (this.state) return this.state;
		if (this.tree) this.state = this.getTreeState();
		return this.state;
	}


	_onLoad = () => {
		if (this.state) {
			this.setTreeState(this.state);
		}
		this.dispatchEvent(new BrowserEvent('load', {browser: this}));
	}

	_onTreeChange = (e: TreeEvent) => {
		this.state = null;
		this.dispatchEvent(new BrowserEvent('state', {browser: this, node: e.node}));
	}

	refresh() {
		this.state = this.getTreeState();
		this.tree.loadData();
	}

	deleteNode(node: NodeModel) {
		let target = node;
		while (target.parent && target.parent.level > -1 && target.parent.children.length == 1) {
			target = target.parent;
		}
		this.tree.model.deleteNodes(target.index, node.index - target.index + 1);
	}

	render = () => html`
		<og-menu ${ref(this.menuRef)}
			@select=${this._onMenuItemSelect}
			maxHeight="300"
			style="display: none; z-index: 2000; min-width: 150px">
		</og-menu>

		<og-tree
			.adapter="${this.adapter}"
			@select="${this._onSelect}"
			@tree.menu="${this._onMenu}"
			@tree.collapse="${this._onTreeChange}"
			@load="${this._onLoad}">
		</og-tree>

		<og-tooltip ${ref(this.tooltipRef)} color="accent" style="max-width: 300px; display: none; z-index: 499">
			<div style="padding: 2px" slot="body"></div>
		</og-tooltip>
	`;

}
